import { Product, ResultsInfo } from "@~/types/types";
import { useCallback, useEffect, useState } from "react";
import fetchSocialProofData from "./socialProofData";
import { SocialProofMessages } from "@~/contexts/SocialProofMessagesContext/SocialProofMessagesContext";

type Props = {
  resultsInfo: ResultsInfo;
  products: Product[];
  appId: string;
};

export const filterExistingProducts = (
  products: Product[],
  existingProducts: Product[],
) => {
  return products.filter((product) => {
    return !existingProducts.some((existing: Product) => {
      return existing.aliases.pimProductId === product.aliases.pimProductId;
    });
  });
};

export function useSocialProofMessaging({
  resultsInfo,
  products,
  appId,
}: Props) {
  const [socialProofData, setSocialProofData] = useState<SocialProofMessages[]>(
    [],
  );
  const [existingProducts, setExistingProducts] = useState<Product[]>([]);

  const retrieveSocialProofData = useCallback(() => {
    if (socialProofData.length > resultsInfo.total) {
      setSocialProofData([]);
      setExistingProducts([]);
      return;
    }

    if (resultsInfo.total > 0 && products.length > 0) {
      const filteredProducts = filterExistingProducts(
        products,
        existingProducts,
      );

      if (filteredProducts.length > 0) {
        fetchSocialProofData(filteredProducts, appId)
          .then((data) => {
            setSocialProofData((currentSocialProofData) => [
              ...currentSocialProofData,
              ...data,
            ]);
          })
          .catch(() => {
            // Social proof is non-critical; ignore API errors to avoid unhandled rejections.
          });
        setExistingProducts((currentExistingProducts) => [
          ...currentExistingProducts,
          ...filteredProducts,
        ]);
      }
    }
  }, [
    appId,
    existingProducts,
    products,
    resultsInfo.total,
    socialProofData.length,
  ]);

  useEffect(
    function loadSocialProofData() {
      if (appId) {
        retrieveSocialProofData();
      }
    },
    [appId, retrieveSocialProofData],
  );

  return socialProofData;
}
